/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToWishlistComponent, IWishlistActionErrorResult } from '@msdyn365-commerce/components';
import { CommerceProperty, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import {
    getConfigureErrors,
    IBuyboxAddToCartViewProps,
    IBuyboxCallbacks,
    IBuyboxCommonResources,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxState,
    IQuickviewViewProps
} from '@msdyn365-commerce-modules/buybox';
import { ISelectedProduct, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IQuickviewProps as IQuickviewPropsExtend } from '../definition-extensions/quickview.ext.props.autogenerated';

/**
 * RenderAddToCart.
 * @param addToCart - AddToCart.
 * @returns -- Returns.
 */
const renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

/**
 * Add to wishlist failed function.
 * @param callbacks -Buybox callbacks.
 * @param resources -Buybox resources.
 * @param product -Simple product.
 * @returns Update error state.
 */
const onAddToWishlistFailed = (
    callbacks: IBuyboxCallbacks | undefined,
    resources: IBuyboxCommonResources,
    product: SimpleProduct | undefined
) => (result: IWishlistActionErrorResult) => {
    callbacks?.updateErrorState({
        errorHost: 'WISHLIST',
        configureErrors:
            result.status === 'MISSINGDIMENSION' ? getConfigureErrors(result.missingDimensions, resources, product?.IsGiftCard) : {}
    });
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox viewprops.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @param product - Product data.
 * @returns -- Returns.
 */
const renderAddtoWishlistButton = (
    props: IQuickviewViewProps,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks | undefined,
    product: SimpleProduct
): React.ReactNode => {
    const { context, resources } = props;
    const wishlists = props.data.wishlists.result;
    const isShowWishlitButtonText = true;

    const addToWishlistButtonText = 'Add to Wishlist';
    const removeFromWishlistButtonText = 'Remove from Wishlist';
    const addToWishlistMessage = 'Added to your Wishlist';
    const removedFromWishlistMessage = 'Item removed from Wishlist';
    const addItemToWishlistError = 'Unable to add item to Wishlist';
    const removeItemFromWishlistError = 'Unable to remove item from Wishlist';
    const nameOfWishlist = 'My Wishlist';
    return (
        <AddToWishlistComponent
            className='msc-add-to-cart-extra-actions'
            addToWishlistButtonText={addToWishlistButtonText}
            removeFromWishlistButtonText={removeFromWishlistButtonText}
            addToWishlistMessage={addToWishlistMessage}
            removedFromWishlistMessage={removedFromWishlistMessage}
            addItemToWishlistError={addItemToWishlistError}
            removeItemFromWishlistError={removeItemFromWishlistError}
            nameOfWishlist={nameOfWishlist}
            data={{ product, wishlists }}
            context={context}
            id={props.id}
            typeName={props.typeName}
            onError={onAddToWishlistFailed(callbacks, resources, product)}
            getSelectedProduct={state.selectedProduct}
            showButtonText={isShowWishlitButtonText}
            showButtonTooltip={false}
        />
    );
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox viewprops.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToWishlist = (
    props: IQuickviewViewProps,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks | undefined
): JSX.Element | null => {
    const product = props.productDetails?.product;
    if (!props.addToWishlist || !product) {
        return null;
    }

    const wishlistButton = renderAddtoWishlistButton(props, state, callbacks, product);
    return (
        <Node {...props.addToWishlist.ContainerProps}>
            {props.addToWishlist.errorBlock}
            {wishlistButton}
        </Node>
    );
};

/**
 * RenderQuantity.
 * @param quantity - Quantity.
 * @param quantityLimitsMessages - QuantityLimitsMessages.
 * @param props - Props.
 * @returns -- Returns.
 */
const renderQuantity = (
    quantity: IBuyboxProductQuantityViewProps,
    quantityLimitsMessages: React.ReactNode,
    props: IQuickviewViewProps
): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
    const { ContainerProps, LabelContainerProps, heading, input, errors } = quantity;
    const { unitOfMeasure } = props;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
            {unitOfMeasure}
            {quantityLimitsMessages}
            {errors}
        </Node>
    );
};

/**
 * KenderKeyInPrice.
 * @param keyInPrice - KeyInPrice.
 * @returns -- Returns.
 */
const renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

/**
 * RenderConfigureDropdown.
 * @param dropdown - Buybox product configure dropdown view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {select}
            {errors}
        </Node>
    );
};

/**
 * RenderConfigure.
 * @param configure - Buybox product configure view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(renderConfigureDropdown)}</Node>;
};

/**
 * RenderBodyContent.
 * @param props - Props.
 * @returns -- Returns.
 */
const renderBodyContent = (props: IQuickviewViewProps & IQuickviewPropsExtend<{}>): JSX.Element => {
    const {
        price,
        keyInPrice,
        quantity,
        inventoryLabel,
        quantityLimitsMessages,
        loading,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
        ProductInfoContainerProps,
        MediaGalleryContainerProps,
        CarouselProps,
        addToWishlist,
        configure,
        callbacks,
        state
    } = props;
    if (loading) {
        return <>{loading}</>;
    }

    const VAT_RATE = 1.23;

    const processPrice = (price: React.ReactNode): React.ReactNode => {
        if (React.isValidElement(price) && price.props?.data?.price) {
            const priceData = price.props.data.price;

            const base =
                typeof priceData.ActivePrice === 'number'
                    ? priceData.ActivePrice
                    : typeof priceData.AdjustedPrice === 'number'
                    ? priceData.AdjustedPrice - (priceData.DiscountAmount ?? 0)
                    : undefined;

            if (typeof base === 'number') {
                const gross = Math.round(base * VAT_RATE * 100) / 100;
                return <span className='msc-buybox__price'>€{gross.toFixed(2)}</span>;
            }
        }
        return price;
    };

    const isB2B = props.context?.request?.apiSettings?.channelCustomerType === 'B2B';
    const vatLabel = isB2B ? ' (ex VAT)' : ' (inc VAT)';
    return (
        <>
            <Node {...MediaGalleryContainerProps}>
                <Node {...CarouselProps} />
            </Node>
            <Node {...ProductInfoContainerProps}>
                <Node className='msc-quickview__price-section'>
                    <Node className='msc-quickview__price-section-text'>
                        {props.resources.priceText}
                        <span className='msc-price'>
                            <span className='msc-price__actual'>{isB2B ? price : processPrice(price)}</span>
                            <span className='vat-text vat-below'> {vatLabel}</span>
                        </span>
                    </Node>
                    {isB2B ? addToWishlist && renderAddToWishlist(props, state, callbacks) : ''}
                </Node>
                {configure && renderConfigure(configure)}
                {keyInPrice && renderKeyInPrice(keyInPrice)}
                {quantity && renderQuantity(quantity, quantityLimitsMessages, props)}
                {inventoryLabel}
            </Node>
        </>
    );
};

/**
 * RenderQuickViewPopup.
 * @param props - Props.
 * @returns -- Returns.
 */
const renderQuickViewPopup = (props: IQuickviewViewProps & IQuickviewPropsExtend<{}>): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Quickview.tsx file
    const {
        ModalContainer,
        ModalHeaderContainer,
        ModalFooterContainer,
        ModalHeaderContent,
        ModalBodyContainer,
        addToCart,
        cartContainerProps,
        seeDetailsbutton
    } = props;

    return (
        <Module {...ModalContainer}>
            <Node {...ModalHeaderContainer}>
                {ModalHeaderContent}
                <Node className='msc-quickview__heading'>
                    {props.title}
                    {!props.loading && !StringExtensions.isNullOrEmpty(props.state.productPrice?.ItemId) ? (
                        <div>
                            <Node className='msc-quickview__brand'>{getBrandName(props, props.productDetails?.product)}</Node>
                            <Node className='msc-quickview__sku-text'>
                                {props.resources.skuText}
                                {props.state.productPrice?.ItemId}
                            </Node>
                        </div>
                    ) : (
                        ''
                    )}
                    {props.rating}
                </Node>
            </Node>
            <Node {...ModalBodyContainer}>{renderBodyContent(props)}</Node>
            <Node {...ModalFooterContainer}>
                <Node {...cartContainerProps}>
                    {addToCart && seeDetailsbutton}
                    {addToCart && renderAddToCart(addToCart)}
                </Node>
            </Node>
        </Module>
    );
};

export function getBrandName(
    props: IQuickviewViewProps & IQuickviewPropsExtend<{}>,
    product: ISelectedProduct | undefined | null
): React.ReactElement | undefined {
    const app = props.context.app;
    if (app.config.hsoRexelBrandName && product) {
        const hsoBrandNameAttribute = (app.config.hsoRexelBrandName && app.config.hsoRexelBrandNameAttribute.toUpperCase()) || 'BRAND';
        const hsoBrand = hsoBrandNameAttribute && getExtensionPropertyText(hsoBrandNameAttribute, product?.ExtensionProperties);
        if (hsoBrand != undefined && hsoBrand != '') {
            const redirectURL = '/new-category/energy-management/5637295326.c?refiners=[[5,"<BRAND>",5637150576,1,"<BRAND>","",0]]';
            const rebrandedURL = redirectURL.split('<BRAND>').join(hsoBrand);
            const navigationURL = props.context.request.sitePath + rebrandedURL;
            return (
                <span className='msc-buybox__brand-name'>
                    by <a href={navigationURL}>{hsoBrand}</a>
                </span>
            );
        }
    }
    return undefined;
}

function getExtensionPropertyText(attributeName: string, productAttributes: CommerceProperty[] | undefined): string | undefined {
    if (productAttributes && productAttributes.length) {
        const filterAttributes = productAttributes.filter(
            attribute => attribute.Key && attribute.Key.toUpperCase() === attributeName.toUpperCase()
        );
        return filterAttributes && filterAttributes.length > 0 && filterAttributes[0].Value
            ? filterAttributes[0].Value.StringValue
            : undefined;
    }
    return;
}
/**
 * Functional component which renders quick view button and popup based by the given props file.
 * @param props - Configuration for the quick view component.
 * @returns - Functional component of the quick view.
 */
export const QuickViewFunctionalComponent: React.FC<IQuickviewViewProps & IQuickviewPropsExtend<{}>> = (
    props: IQuickviewViewProps & IQuickviewPropsExtend<{}>
) => {
    const { quickView, quickViewButton, isModalOpen } = props;
    const quickViewUpdatedProps: IModuleProps = { ...quickView, title: props.resources.quickViewbuttonText };
    return (
        <Module {...quickViewUpdatedProps}>
            {quickViewButton}
            {isModalOpen && renderQuickViewPopup(props)}
        </Module>
    );
};

export default QuickViewFunctionalComponent;
